import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/Edit";
import ExpandIcon from "@mui/icons-material/ExpandMore";
import {
  Alert,
  Box,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../context/CaseContext";
import { useCurrency } from "../context/CurrencyContext.tsx";
import { useTheme } from "../context/ThemeContext";
import { NamedValue, Party } from "../shared/CaseModel";
import { getValueOfAssets } from "../shared/CaseModelUtility";
import {
  claimsAgainstParty,
  claimsRemovedWithParty,
} from "../shared/partyRemoval";
import { AssetList } from "./AssetList";
import { PartyAvatar } from "./PartyAvatar";
import { PartyCallbacks } from "./Parties";
import { PartyEditorDialog } from "./PartyEditorDialog";
import { TABULAR_NUMERALS } from "./general/dataAtoms/shared";
import { SectionLabel } from "./general/SectionLabel";
import { Marked } from "./guide/GuideMarker";
import { SegmentedControl } from "./general/SegmentedControl";
import { TonalBadge } from "./general/TonalBadge";
import { YesNoDialog } from "./general/YesNoDialog";

export const PartyDetails = ({
  party,
  partyCallbacks,
}: {
  party: Party;
  partyCallbacks: PartyCallbacks;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "case.parties" });
  const [expanded, setExpanded] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // the party's own list: there is none while its assets are unlimited
  const assets = Array.isArray(party.assets) ? party.assets : [];
  // store limited assets list in state so we can reset it if user goes to unlimited and back to limited
  // otherwise accidentally going to unlimited would permanently delete the assets and require the user to recreate the list
  const [limitedAssets, setLimitedAssets] = useState<NamedValue[]>(assets);

  const valueOfAssets = getValueOfAssets(party);
  const { currency } = useCurrency();

  // what deleting the party takes with it, as the server applies it on save
  const caseContext = useCase();
  const { readOnly } = caseContext;
  const claims = caseContext.getCase().claims;
  const removedClaims = claimsRemovedWithParty(claims, party.id);
  const claimsAgainst = claimsAgainstParty(claims, party.id).length;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden" }}>
      <Stack
        direction="row"
        spacing={1.75}
        useFlexGap
        onClick={() => setExpanded(!expanded)}
        sx={{
          alignItems: "center",
          flexWrap: "wrap",
          px: 2.25,
          py: 1.75,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <PartyAvatar client={party.client} />

        <Box sx={{ flex: 1, minWidth: theme.spacing(22) }}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ alignItems: "center", flexWrap: "wrap" }}
          >
            <Typography sx={{ fontWeight: 600 }}>{party.name}</Typography>
            {party.client && (
              <TonalBadge tone={theme.palette.primary}>
                {t("clientBadge")}
              </TonalBadge>
            )}
          </Stack>
          <Marked entry="assets">
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
            >
              {valueOfAssets === "unlimited"
                ? t("assets.unlimited")
                : t("assets.value", { value: currency.format(valueOfAssets) })}
            </Typography>
          </Marked>
        </Box>

        <Stack
          direction="row"
          sx={{ alignItems: "center" }}
          // the row around these buttons opens and closes the assets
          onClick={(event) => event.stopPropagation()}
        >
          {/* each tooltip needs an element that fires events, which a
              disabled button does not */}
          <Tooltip title={t("tip.edit")}>
            <span>
              <IconButton
                size="small"
                aria-label={t("tip.edit")}
                disabled={readOnly}
                onClick={() => setEditDialogOpen(true)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("tip.delete")}>
            <span>
              <IconButton
                size="small"
                aria-label={t("tip.delete")}
                disabled={readOnly}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ "&:hover": { color: theme.palette.data.negative.dark } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 0.5 }} />
          <IconButton
            size="small"
            aria-expanded={expanded}
            aria-label={t(expanded ? "assets.tip.hide" : "assets.tip.show")}
            onClick={() => setExpanded(!expanded)}
            sx={{ bgcolor: expanded ? "action.hover" : undefined }}
          >
            <ExpandIcon
              sx={{
                transition: theme.transitions.create("transform"),
                transform: expanded ? "rotate(180deg)" : undefined,
              }}
            />
          </IconButton>
        </Stack>
      </Stack>

      <Collapse in={expanded}>
        <Stack
          spacing={1.5}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            px: 2.25,
            pt: 2,
            pb: 2.25,
          }}
        >
          <Stack
            direction="row"
            spacing={1.75}
            useFlexGap
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <SectionLabel>{t("assets.assetOptions.title")}</SectionLabel>
            <SegmentedControl
              ariaLabel={t("assets.assetOptions.title")}
              disabled={readOnly}
              value={valueOfAssets === "unlimited" ? "unlimited" : "limited"}
              options={[
                {
                  value: "unlimited",
                  label: t("assets.assetOptions.unlimited"),
                },
                { value: "limited", label: t("assets.assetOptions.limited") },
              ]}
              onChange={(choice) =>
                partyCallbacks.setAssets(
                  party,
                  choice === "unlimited" ? "unlimited" : limitedAssets,
                )
              }
            />
          </Stack>

          {valueOfAssets !== "unlimited" && (
            <AssetList
              assets={assets}
              total={valueOfAssets}
              readOnly={readOnly}
              onChange={(newAssets) => {
                setLimitedAssets(newAssets);
                partyCallbacks.setAssets(party, newAssets);
              }}
            />
          )}
        </Stack>
      </Collapse>

      <PartyEditorDialog
        party={party}
        open={editDialogOpen}
        onClose={(updatedParty) => {
          if (updatedParty && updatedParty.id === party.id) {
            if (updatedParty.name !== party.name) {
              partyCallbacks.setName(party, updatedParty.name);
            }
            if (updatedParty.client !== party.client) {
              if (updatedParty.client) partyCallbacks.setClient(party);
              else partyCallbacks.setNonClient(party);
            }
          }
          setEditDialogOpen(false);
        }}
      />
      <YesNoDialog
        onYes={() => {
          partyCallbacks.delete(party);
          setDeleteDialogOpen(false);
        }}
        yesColor="error"
        onNo={() => {
          setDeleteDialogOpen(false);
        }}
        open={deleteDialogOpen}
        title={t("deleteDialog.title")}
        description={t("deleteDialog.description", {
          partyName: party.name,
        })}
      >
        {removedClaims.length > 0 && (
          <Alert severity="warning">
            {t("deleteDialog.claims", {
              count: removedClaims.length,
              partyName: party.name,
            })}
            <List dense disablePadding>
              {removedClaims.map(({ id, title }) => (
                <ListItem key={id} disableGutters disablePadding>
                  <ListItemText
                    primary={title}
                    slotProps={{ primary: { sx: { fontWeight: 600 } } }}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
        {claimsAgainst > 0 && (
          <Alert severity="warning">
            {t("deleteDialog.defendant", {
              count: claimsAgainst,
              partyName: party.name,
            })}
          </Alert>
        )}
      </YesNoDialog>
    </Paper>
  );
};
